import { Code, ConnectError } from '@connectrpc/connect'

// ResolvePortalLink outcomes the /p/$code route already renders as a page
// ("Link expired" / "Link unavailable") and the backend already records as a
// portal-link event. Reporting them from the browser too only adds noise
// (Sentry CORTADO-PORTAL-X, CORTADO-PORTAL-2).
const expectedResolvePortalLinkCodes = new Set<Code>([Code.FailedPrecondition, Code.NotFound])

/**
 * Decides whether a failed portal RPC is worth a Sentry event. The transport
 * interceptor calls this for every error before calling reportError.
 */
export function shouldReportRpcError(error: unknown, methodName: string): boolean {
  // Expected auth errors are handled in-page.
  if (error instanceof ConnectError && error.code === Code.Unauthenticated) return false

  const connectErr = ConnectError.from(error)

  // The request was aborted on purpose: a query observer unmounted during
  // navigation, or an optimistic update called cancelQueries (Sentry
  // CORTADO-PORTAL-H, CORTADO-PORTAL-K).
  if (connectErr.code === Code.Canceled) return false

  // Browser-level network failures: the request never reached the server.
  if (
    connectErr.code === Code.Unknown &&
    /Failed to fetch|NetworkError|Load failed/i.test(connectErr.rawMessage)
  ) {
    return false
  }

  if (methodName === 'ResolvePortalLink' && expectedResolvePortalLinkCodes.has(connectErr.code)) {
    return false
  }

  return true
}
